import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Segmented, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import QualityNote from '../../shared-components/analytics/QualityNote';
import StatTile from '../../shared-components/analytics/StatTile';
import BarList from '../../shared-components/charts/BarList';
import { getSalesHourly } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';

const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

function AnalyticsTraffic() {
  const { t } = useTranslation();
  const [byDayPart, setByDayPart] = useState(false);
  const [splitWeekend, setSplitWeekend] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getSalesHourly({ groupByDayPart: byDayPart, splitWeekend })
      .then((d) => { setResult(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [byDayPart, splitWeekend]);

  const rows = result?.items ?? [];
  const label = (r) => {
    const base = byDayPart ? r.dayPartName : hourLabel(r.hourOfDay);
    if (!splitWeekend) return base;
    return `${base} · ${r.isWeekend ? t('analytics_weekend') : t('analytics_weekday')}`;
  };

  const totalOrders = _.sumBy(rows, 'orderCount');
  const peak = _.maxBy(rows, 'orderCount');
  const bestBasket = _.maxBy(rows.filter((r) => r.averageBasketValue != null), 'averageBasketValue');

  return (
    <AnalyticsPage
      title={t('analytics_traffic_title')}
      subtitle={t('analytics_traffic_subtitle')}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={t('analytics_orders')} value={thousand(totalOrders)} emphasis />
        <StatTile
          label={t('analytics_peak')}
          value={peak ? label(peak) : '—'}
          sub={peak ? `${thousand(peak.orderCount)} ${t('analytics_orders').toLowerCase()}` : null}
        />
        <StatTile
          label={t('analytics_best_basket')}
          value={bestBasket ? toCurrency(bestBasket.averageBasketValue) : '—'}
          sub={bestBasket ? label(bestBasket) : null}
          hint={t('analytics_avg_basket_help')}
        />
        <StatTile
          label={t('analytics_avg_basket')}
          value={toCurrency(totalOrders > 0 ? _.sumBy(rows, 'productsTotal') / totalOrders : null)}
        />
      </div>

      {/* Two labelled choices rather than a segmented control next to a bare
          toggle -- the toggle read as an unrelated setting instead of the second
          half of "how should this be sliced". */}
      <div className="mb-5 rounded-card border border-line bg-surface p-4 shadow-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-7">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
              {t('analytics_group_by')}
            </div>
            <Segmented
              value={byDayPart}
              onChange={setByDayPart}
              options={[
                { label: t('analytics_by_hour'), value: false },
                { label: t('analytics_by_day_part'), value: true },
              ]}
            />
          </div>

          <div className="hidden w-px self-stretch bg-line sm:block" />
          <div className="h-px w-full bg-line sm:hidden" />

          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
              {t('analytics_compare')}
            </div>
            <Segmented
              value={splitWeekend}
              onChange={setSplitWeekend}
              options={[
                { label: t('analytics_all_days'), value: false },
                { label: t('analytics_weekday_vs_weekend'), value: true },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 text-sm font-semibold text-ink">{t('analytics_orders_by_period')}</div>
          <BarList
            rows={_.map(rows, (r, i) => ({ id: i, label: label(r), value: r.orderCount }))}
            formatValue={(v) => thousand(v)}
            barHeight={9}
            renderTooltip={(row) => {
              const full = rows[row.id];
              return (
                <div className="text-xs tabular-nums" style={{ minWidth: '13rem' }}>
                  <div className="mb-1 font-semibold">{row.label}</div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_orders')}</span>
                    <span>{thousand(full?.orderCount)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_revenue')}</span>
                    <span>{toCurrency(full?.productsTotal)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_avg_basket')}</span>
                    <span>{toCurrency(full?.averageBasketValue)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_share_of_orders')}</span>
                    <span>{pct(full?.shareOfOrdersPct)}</span>
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 text-sm font-semibold text-ink">{t('analytics_detail')}</div>
          <Table
            size="small"
            bordered={false}
            loading={loading}
            rowKey={(r) => `${r.dayPartName ?? r.hourOfDay}-${r.isWeekend}`}
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={rows}
            locale={{ emptyText: t('no_data') }}
            columns={[
              {
                title: byDayPart ? t('analytics_day_part') : t('analytics_hour'),
                key: 'label',
                render: (r) => <span className="text-sm text-ink">{label(r)}</span>,
              },
              {
                title: t('analytics_orders'),
                key: 'orders',
                align: 'right',
                render: (r) => <span className="tabular-nums">{thousand(r.orderCount)}</span>,
              },
              {
                title: t('analytics_avg_basket'),
                key: 'basket',
                align: 'right',
                render: (r) => <span className="tabular-nums">{toCurrency(r.averageBasketValue)}</span>,
              },
              {
                title: t('analytics_share_of_orders'),
                key: 'share',
                align: 'right',
                render: (r) => <span className="tabular-nums text-muted">{pct(r.shareOfOrdersPct)}</span>,
              },
            ]}
          />
          <p className="mt-3 text-xs leading-relaxed text-muted">
            {t('analytics_day_part_note')}
          </p>
          <QualityNote quality={result?.quality} className="mt-3 border-t border-line pt-3" />
        </div>
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsTraffic;
